import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    Star,
    Quote,
    X,
    Save,
    GripVertical,
} from 'lucide-react';
import Notification from '../Common/Notification';
import { storefrontGet, storefrontPost, storefrontPut, storefrontDelete } from '../../services/storefrontApi';
import ActionMenu from '../Common/ActionMenu';

const StarSelector = ({ value, onChange }) => (
    <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(s => (
            <button
                key={s}
                type="button"
                onClick={() => onChange(s)}
                className="focus:outline-none"
            >
                <Star
                    size={20}
                    className={s <= value ? 'text-amber-400 fill-amber-400' : 'text-gray-300 hover:text-amber-200 transition-colors'}
                />
            </button>
        ))}
    </div>
);

/* ─── Testimonial editor ─────────────────────────────────────────────────── */
const TestimonialEditor = ({ testimonial, onClose, onSave, isSaving }) => {
    const isEdit = !!testimonial?.id;
    const [form, setForm] = useState({
        author_name:    testimonial?.author_name    || '',
        author_title:   testimonial?.author_title   || '',
        author_avatar:  testimonial?.author_avatar  || '',
        content:        testimonial?.content        || '',
        rating:         testimonial?.rating         ?? 5,
        featured:       testimonial?.featured       ?? false,
        active:         testimonial?.active         ?? true,
        position:       testimonial?.position       ?? 0,
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {isEdit ? 'Modifier le témoignage' : 'Nouveau témoignage'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-5 space-y-4 max-h-[78vh] overflow-y-auto">
                    {/* Avatar preview */}
                    {form.author_avatar && (
                        <div className="flex justify-center">
                            <img
                                src={form.author_avatar}
                                alt={form.author_name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-primary/30"
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Nom *</label>
                            <input type="text" value={form.author_name} onChange={e => set('author_name', e.target.value)}
                                placeholder="Fatima Zahra"
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Titre / Rôle</label>
                            <input type="text" value={form.author_title} onChange={e => set('author_title', e.target.value)}
                                placeholder="Cliente fidèle"
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Photo (URL)</label>
                        <input type="url" value={form.author_avatar} onChange={e => set('author_avatar', e.target.value)}
                            placeholder="https://cdn.example.com/avatar.jpg"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Témoignage *</label>
                        <textarea
                            value={form.content}
                            onChange={e => set('content', e.target.value)}
                            rows={4}
                            placeholder="Un service exceptionnel..."
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary resize-none"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-2 block">Note</label>
                            <StarSelector value={form.rating} onChange={v => set('rating', v)} />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Position</label>
                            <input type="number" min={0} value={form.position} onChange={e => set('position', parseInt(e.target.value, 10) || 0)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                    </div>

                    <div className="space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                        {[
                            { key: 'featured', label: 'Mettre en avant' },
                            { key: 'active',   label: 'Actif' },
                        ].map(({ key, label }) => (
                            <div key={key} className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
                                <button
                                    type="button"
                                    onClick={() => set(key, !form[key])}
                                    className={`relative h-6 w-11 rounded-full transition-colors ${form[key] ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                                >
                                    <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${form[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={isSaving || !form.author_name || !form.content}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Testimonial card ──────────────────────────────────────────────────── */
const TestimonialCard = ({ testimonial, onEdit, onDelete }) => (
    <div className={`bg-white dark:bg-gray-800 border rounded-xl p-5 group transition-opacity ${!testimonial.active ? 'opacity-50' : ''} border-gray-200 dark:border-gray-700`}>
        <div className="flex justify-between items-start gap-3 mb-3">
            <div className="flex items-center gap-3">
                {testimonial.author_avatar ? (
                    <img src={testimonial.author_avatar} alt={testimonial.author_name}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                        onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary text-sm font-semibold">
                            {testimonial.author_name?.slice(0, 2).toUpperCase()}
                        </span>
                    </div>
                )}
                <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{testimonial.author_name}</p>
                    {testimonial.author_title && <p className="text-xs text-gray-500">{testimonial.author_title}</p>}
                </div>
            </div>
            <ActionMenu
                label={`Actions pour ${testimonial.author_name || ''}`}
                items={[
                    {label: 'Modifier', icon: Pencil, onClick: () => onEdit(testimonial)},
                    {divider: true},
                    {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(testimonial)},
                ]}
            />
        </div>

        <div className="flex gap-0.5 mb-2">
            {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} size={13} className={s <= testimonial.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
            ))}
        </div>

        <div className="relative">
            <Quote size={20} className="absolute -top-1 -left-1 text-primary/20 rotate-180" />
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed pl-4 line-clamp-3">{testimonial.content}</p>
        </div>

        <div className="flex gap-2 mt-3">
            {testimonial.featured && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">⭐ En avant</span>
            )}
            {!testimonial.active && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactif</span>
            )}
        </div>
    </div>
);

/* ─── Main component ─────────────────────────────────────────────────────── */
const TestimonialsSettings = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editorTestimonial, setEditorTestimonial] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type !== 'loading') setTimeout(() => setNotification(n => ({ ...n, show: false })), 3000);
    };

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await storefrontGet('/testimonials');
            const data = res.data?.data ?? res.data ?? [];
            setTestimonials(Array.isArray(data) ? data : []);
        } catch {
            showNotification('error', 'Impossible de charger les témoignages.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (form) => {
        setIsSaving(true);
        try {
            if (editorTestimonial?.id) {
                await storefrontPut(`/testimonials/${editorTestimonial.id}`, form);
                showNotification('success', 'Témoignage mis à jour.');
            } else {
                await storefrontPost('/testimonials', form);
                showNotification('success', 'Témoignage créé.');
            }
            setEditorTestimonial(undefined);
            load();
        } catch {
            showNotification('error', 'Erreur lors de l\'enregistrement.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (t) => {
        if (!window.confirm(`Supprimer le témoignage de « ${t.author_name} » ?`)) return;
        try {
            await storefrontDelete(`/testimonials/${t.id}`);
            setTestimonials(prev => prev.filter(x => x.id !== t.id));
            showNotification('success', 'Témoignage supprimé.');
        } catch {
            showNotification('error', 'Impossible de supprimer ce témoignage.');
        }
    };

    const sorted = [...testimonials].sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return (a.position ?? 0) - (b.position ?? 0);
    });

    const avgRating = testimonials.length
        ? (testimonials.reduce((s, t) => s + (t.rating || 0), 0) / testimonials.length).toFixed(1)
        : '—';

    return (
        <div className="px-4 py-6 max-w-5xl mx-auto">
            <Notification show={notification.show} type={notification.type} message={notification.message} onClose={() => setNotification(n => ({ ...n, show: false }))} />

            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-base font-semibold text-gray-800 dark:text-white">Témoignages clients</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {testimonials.length} témoignage(s) · Note moyenne : {avgRating}
                        {avgRating !== '—' && <Star size={11} className="inline ml-1 fill-amber-400 text-amber-400" />}
                    </p>
                </div>
                <button
                    onClick={() => setEditorTestimonial(null)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors"
                >
                    <Plus size={16} /> Ajouter un témoignage
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <RefreshCw size={20} className="animate-spin text-primary" />
                </div>
            ) : testimonials.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <Quote size={40} className="mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Aucun témoignage client.</p>
                    <button onClick={() => setEditorTestimonial(null)} className="mt-3 text-primary text-sm hover:underline">
                        Ajouter le premier témoignage
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sorted.map(t => (
                        <TestimonialCard
                            key={t.id}
                            testimonial={t}
                            onEdit={setEditorTestimonial}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {editorTestimonial !== undefined && (
                <TestimonialEditor
                    testimonial={editorTestimonial}
                    onClose={() => setEditorTestimonial(undefined)}
                    onSave={handleSave}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default TestimonialsSettings;
